import { AutocompleteWithLabel } from '@adotel/components'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import PageHeader from '../../components/PageHeader'
import SectionTitle from '../../components/SectionTitle'
import {
  PROVIDERS_SUBMIT_GENERATE_MUTATION,
  PROVIDERS_SUBMIT_LIST_QUERY,
  PROVIDERS_SUBMIT_MODIFY_MUTATION
} from '../../graphql/accommodationProviderSubmit'
import { PROVIDERS_QUERY } from '../../graphql/providerQueries'
import { getTranslate, lang } from '../../localization'
import { ProviderAdapter } from '../../types/accommodation'
import {
  ProviderConnectionsType,
  ProviderConnectionsStatus,
  ProviderSubmit
} from '../../types/accommodationProviderSubmit'
import { ProviderBrief } from '../../types/provider'
import ProviderSubmitBox from './ProviderSubmitBox'
import ProviderSubmitSkeleton from './ProviderSubmitSkeleton'
import useStyles from './Styles/index.style'
import { defaultAddOns } from './defaultAddOns'

const defaultConnections: ProviderConnectionsType = {
  status: 'InActive' as ProviderConnectionsStatus,
  availability: {
    providerAdapter: '',
    providerURL: '',
    manualARR: true,
    pmsConnected: true
  },
  rate: {
    providerAdapter: '',
    providerURL: '',
    manualARR: true,
    pmsConnected: true,
    readRatePlanDataFromPMS: true
  },
  restriction: {
    providerAdapter: '',
    providerURL: '',
    manualARR: true,
    pmsConnected: true,
    readRatePlanDataFromPMS: true
  },
  booking: {
    bookingNotesEnabled: false,
    bookingNotesSupportedAdapters: [],
    providerAdapter: '',
    providerURL: '',
    pmsCommunicationMode: 'AlwaysSync',
    bookingTimeout: 60,
    occupancyPercentThreshold: 0,
    pmsConnected: true
  }
}

interface AccommodationProviderSubmitProp {
  title: string
}

export default function AccommodationProviderSubmit({
  title
}: AccommodationProviderSubmitProp) {
  const classes = useStyles()
  const params = useParams<{
    propertyCode: string
  }>()
  const [autoCompleteForProvider, setAutoCompleteForProvider] = useState<any[]>(
    []
  )

  const [selectedProviderSubmit, setSelectedProviderSubmit] = useState<
    ProviderSubmit[]
  >([])

  const { data: providerData, loading: loadingForProvider } = useQuery<{
    providers: ProviderBrief[]
  }>(PROVIDERS_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const [
    getProviderList,
    { data: providerListData, loading: loadingForProviderSubmitList, refetch }
  ] = useLazyQuery<{
    providersSubmitList: ProviderSubmit[]
  }>(PROVIDERS_SUBMIT_LIST_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const updatedProvidersSubmitList = data.providersSubmitList.map(
        (provider) => {
          const hasAddOns = provider.addOns && provider.addOns.length > 0

          // Stored notificationSettings pass through untouched and are no
          // longer defaulted. Seeding them here is what wrote an empty,
          // inactive structure onto every provider that lacked one — 5,687
          // entities in production carried that scaffolding while only 109
          // held a real address.
          return {
            ...provider,
            addOns: hasAddOns ? provider.addOns : defaultAddOns
          }
        }
      )
      setSelectedProviderSubmit(updatedProvidersSubmitList)
    },
    onError: (error) => {
      toast.error(error.message)
    }
  })
  const [providersSubmitGenerate, { loading: loadingForProvidergenerate }] =
    useMutation(PROVIDERS_SUBMIT_GENERATE_MUTATION, {
      notifyOnNetworkStatusChange: true,
      onCompleted: (response) => {
        refetch()
        toast.success(response.providersSubmitGenerate.message)
      },
      onError: (error) => {
        toast.error(error.message)
      }
    })
  const [providersSubmitModify, { loading: loadingForProviderModify }] =
    useMutation(PROVIDERS_SUBMIT_MODIFY_MUTATION, {
      notifyOnNetworkStatusChange: true,
      onCompleted: (response) => {
        refetch()
        toast.success(response.providersSubmitModify.message)
      },
      onError: (error) => {
        toast.error(error.message)
      }
    })
  useEffect(() => {
    if (loadingForProvider || !providerData) {
      return
    }

    setAutoCompleteForProvider(
      selectedProviderSubmit.map((ele) => ({
        value: ele.providerCode,
        code: ele.displayProviderCode,
        label:
          lang === 'en'
            ? `${ele.providerName} - (${ele.displayProviderCode})`
            : `${
                providerData?.providers.find(
                  (p) => p.displayProviderCode == ele.displayProviderCode
                )?.name
              } - (${ele.displayProviderCode})`
      }))
    )
  }, [selectedProviderSubmit, loadingForProvider, providerData])

  const existProviderCodes = providerListData?.providersSubmitList.map(
    (p) => p.providerCode
  )
  useEffect(() => {
    getProviderList({
      variables: {
        propertyCode: params.propertyCode
      }
    })
  }, [])

  return (
    <div>
      <PageHeader title={title} />
      <div className={classes.header}>
        <SectionTitle title={getTranslate('Provider Submit')} marginBottom />
      </div>
      <div>
        <AutocompleteWithLabel
          multiple
          label={getTranslate('Provider')}
          placeholder={getTranslate('Select Provider')}
          inputSize="small"
          limitTags={5}
          getOptionLabel={(option) => option?.label || ''}
          value={autoCompleteForProvider}
          getOptionSelected={(option, value) => option.value === value.value}
          onChange={(_, v: any[]) => {
            const providerCodes = selectedProviderSubmit.map(
              (ele) => ele.displayProviderCode
            )
            const newItems = v.filter(
              (item) => !providerCodes.includes(item.code)
            )

            const updatedProviderSubmit = selectedProviderSubmit.filter(
              (item) =>
                v.some(
                  (value) => String(value.code) === String(item.providerCode)
                )
            )
            if (newItems.length > 0) {
              const tempProvider: ProviderSubmit[] = [
                ...newItems.map((value: any) => {
                  return {
                    addOns: defaultAddOns,
                    providerCode: value.value,
                    displayProviderCode: value.code,
                    providerConnections: defaultConnections,
                    providerName:
                      providerData?.providers.filter(
                        (p) => p.displayProviderCode === value.code
                      )[0].name || '',
                    providerNameTranslatedList: [
                      {
                        label: 'Iran, Islamic Republic of',
                        nativeLabel: 'فارسی',
                        value:
                          providerData?.providers.filter(
                            (p) => p.displayProviderCode === value.code
                          )[0].nameTranslatedList[0].value || ''
                      }
                    ],
                    ARIRetrievals: [
                      {
                        adapter: '' as ProviderAdapter,
                        retrievalInterval: 1800,
                        lastRetrievalAttempt: '-',
                        nextRetrievalAttempt: '-'
                      }
                    ]
                  }
                })
              ]
              setSelectedProviderSubmit((prev) => [...prev, ...tempProvider])
            } else {
              setSelectedProviderSubmit(updatedProviderSubmit)
            }
          }}
          options={
            providerData?.providers
              ? providerData?.providers.map((p) => ({
                  label: `${p.name} - (${p.displayProviderCode})`,
                  value: p.providerCode,
                  code: p.displayProviderCode
                }))
              : []
          }
          loading={loadingForProvider}
          loadingText={getTranslate('Loading') + '...'}
        />
      </div>
      {loadingForProviderSubmitList ? (
        <ProviderSubmitSkeleton />
      ) : (
        selectedProviderSubmit.map((_, index) => (
          <ProviderSubmitBox
            key={index}
            selectedProviderSubmit={selectedProviderSubmit}
            setSelectedProviderSubmit={setSelectedProviderSubmit}
            selectedProviderSubmitIndex={index}
            providersSubmitGenerate={providersSubmitGenerate}
            propertyCode={params.propertyCode}
            existProviderCodes={existProviderCodes ? existProviderCodes : []}
            loadingForMutation={
              loadingForProvidergenerate || loadingForProviderModify
            }
            providersSubmitModify={providersSubmitModify}
          />
        ))
      )}
    </div>
  )
}
